import { useState, useRef } from 'react';
import { apiClient } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Camera, Shield, Trophy, Calendar, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function getPhotoUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_URL}/api/files/${path}`;
}

export function UserAvatar({ name, photoPath, size = 'md', isChallenger = false, onClick, className = '', interactive = true }) {
  const sizes = { xs: 'w-7 h-7 text-[10px]', sm: 'w-9 h-9 text-xs', md: 'w-12 h-12 text-base', lg: 'w-16 h-16 text-xl', xl: 'w-20 h-20 text-2xl' };
  const badgeSizes = { xs: 'w-3 h-3 -bottom-0 -right-0', sm: 'w-3.5 h-3.5 -bottom-0.5 -right-0.5', md: 'w-4 h-4 -bottom-0.5 -right-0.5', lg: 'w-5 h-5 -bottom-0.5 -right-0.5', xl: 'w-6 h-6 -bottom-0.5 -right-0.5' };
  const sizeClass = sizes[size] || sizes.md;
  const badgeClass = badgeSizes[size] || badgeSizes.md;
  const url = getPhotoUrl(photoPath);
  const initial = name?.charAt(0)?.toUpperCase() || 'U';
  const Tag = interactive ? 'button' : 'div';

  return (
    <Tag onClick={interactive ? onClick : undefined} className={`relative flex-shrink-0 ${className}`} data-testid="user-avatar">
      {url ? (
        <img src={url} alt={name} className={`${sizeClass} rounded-full object-cover border-2 border-[#FF6B35]/40`} />
      ) : (
        <div className={`${sizeClass} rounded-full bg-[#FF6B35]/15 border-2 border-[#FF6B35]/40 flex items-center justify-center`}>
          <span className="text-[#FF6B35] font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{initial}</span>
        </div>
      )}
      {isChallenger && (
        <span className={`absolute ${badgeClass} bg-[#FFB800] rounded-full flex items-center justify-center border-2 border-[#0F0F0F]`} data-testid="challenge-badge">
          <Trophy className="w-2 h-2 text-black" />
        </span>
      )}
    </Tag>
  );
}

export function UserAvatarWithCard({ user, profile, isChallenger = false, size = 'md', className = '' }) {
  const [showCard, setShowCard] = useState(false);

  return (
    <>
      <UserAvatar
        name={user?.name || profile?.name}
        photoPath={user?.profile_photo || profile?.profile_photo}
        size={size}
        isChallenger={isChallenger}
        onClick={() => setShowCard(true)}
        className={className}
      />
      <MemberCardModal
        open={showCard}
        onClose={() => setShowCard(false)}
        user={user}
        profile={profile}
        isChallenger={isChallenger}
      />
    </>
  );
}

export function ProfilePhotoUpload({ currentPhoto, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo trop lourde (max 5 Mo)'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post('/user/photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo de profil mise a jour !');
      if (onUploaded) onUploaded(data.path);
    } catch { toast.error('Erreur upload photo'); }
    finally { setUploading(false); }
  };

  const url = getPhotoUrl(currentPhoto);

  return (
    <div className="relative" data-testid="profile-photo-upload">
      <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-[#FF6B35]/40 bg-[#FF6B35]/15">
        {url ? (
          <img src={url} alt="Profil" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Camera className="w-6 h-6 text-[#FF6B35]/50" />
          </div>
        )}
      </div>
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#FF6B35] rounded-full flex items-center justify-center border-2 border-[#0F0F0F] hover:bg-[#FF8C55] transition-colors"
        data-testid="upload-photo-btn"
      >
        {uploading ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <Camera className="w-4 h-4 text-white" />
        )}
      </button>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
    </div>
  );
}

function MemberCardModal({ open, onClose, user, profile, isChallenger }) {
  const data = profile || user || {};
  const name = data.name || '?';
  const url = getPhotoUrl(data.profile_photo);
  const role = data.role === 'admin' ? 'Coach' : 'Adherent Standard';
  const memberSince = data.created_at ? new Date(data.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '01 / 01 / 2026';
  const memberYear = data.created_at ? new Date(data.created_at).getFullYear() : 2026;
  const memberId = `LM-${memberYear}-${String((data.id || data._id || '0000').slice(-4)).padStart(4, '0')}`;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-transparent border-none p-0 max-w-[320px] shadow-none [&>button]:hidden" data-testid="member-card-modal">
        <DialogTitle className="sr-only">Carte d'adherent</DialogTitle>

        <div style={{ filter: 'drop-shadow(0 0 30px rgba(255,0,184,0.25)) drop-shadow(0 0 60px rgba(255,107,53,0.15)) drop-shadow(0 4px 20px rgba(0,0,0,0.5))' }}>

        {/* OUTER TATTOO BORDER — thick gradient frame */}
        <div className="rounded-[20px] p-[5px]" style={{
          background: 'linear-gradient(135deg, #FF00B8 0%, #FF5533 40%, #FFB800 70%, #FF00B8 100%)',
        }}>
        {/* Inner tribal texture on border */}
        <div className="rounded-[16px] p-[3px] relative" style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, rgba(0,0,0,0.3) 0px, rgba(0,0,0,0.3) 2px, transparent 2px, transparent 6px),
            repeating-linear-gradient(90deg, rgba(0,0,0,0.2) 0px, rgba(0,0,0,0.2) 2px, transparent 2px, transparent 8px),
            linear-gradient(135deg, #FF00B8 0%, #FF5533 40%, #FFB800 70%, #FF00B8 100%)
          `,
        }}>

        {/* CARD INNER */}
        <div className="relative rounded-[13px] overflow-hidden" style={{
          background: '#0A0808',
        }}>
          {/* Radiant glow layers — FLASH */}
          <div className="absolute inset-0 z-0" style={{
            background: 'radial-gradient(circle at 50% -10%, rgba(255,0,184,0.45) 0%, rgba(255,0,184,0.15) 35%, transparent 60%)',
          }} />
          <div className="absolute inset-0 z-0" style={{
            background: 'radial-gradient(circle at 20% 110%, rgba(255,184,0,0.35) 0%, rgba(255,184,0,0.1) 35%, transparent 55%)',
          }} />
          <div className="absolute inset-0 z-0" style={{
            background: 'radial-gradient(circle at 100% 40%, rgba(255,85,51,0.3) 0%, rgba(255,85,51,0.08) 30%, transparent 50%)',
          }} />
          <div className="absolute inset-0 z-0" style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(255,107,53,0.08) 0%, transparent 40%)',
          }} />

          {/* BG: Wolf watermark */}
          <img src="/logo-wolf-t.png" alt="" className="absolute pointer-events-none z-0" style={{
            left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: '80%', height: '80%',
            objectFit: 'contain', opacity: 0.08,
          }} />

          {/* Content */}
          <div className="relative z-10 flex flex-col items-center text-center px-5 pt-5 pb-4">

            {/* Logo — BIG */}
            <img src="/logo-wolf-t.png" alt="" className="w-20 h-20 object-contain" style={{ filter: 'saturate(1.4) brightness(1.15)' }} />

            {/* MEMBRE DE LA MEUTE — BIG */}
            <p className="text-[22px] font-black tracking-[3px] mt-2 leading-none" style={{
              fontFamily: "'Bebas Neue', sans-serif",
              background: 'linear-gradient(90deg, #FF00B8, #FF5533, #FFB800)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 0 8px rgba(255,107,53,0.3))',
            }}>MEMBRE DE LA MEUTE</p>
            <p className="text-[8px] tracking-[5px] font-bold mt-1" style={{
              background: 'linear-gradient(90deg, #FF00B8, #FFB800)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>POLYNESIE</p>

            {/* Gradient line */}
            <div className="h-[1.5px] w-4/5 mt-3.5 mb-4" style={{ background: 'linear-gradient(90deg, transparent, #FF00B8, #FF5533, #FFB800, transparent)' }} />

            {/* Photo */}
            <div className="relative mb-3">
              {url ? (
                <img src={url} alt={name} className="w-[88px] h-[88px] rounded-full object-cover" style={{
                  border: '4px solid transparent',
                  backgroundImage: 'linear-gradient(#080808, #080808), linear-gradient(135deg, #FF00B8, #FF6B35, #FFB800)',
                  backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box',
                  boxShadow: '0 0 20px rgba(255,0,184,0.25), 0 0 40px rgba(255,107,53,0.1)',
                }} />
              ) : (
                <div className="w-[88px] h-[88px] rounded-full flex items-center justify-center" style={{
                  border: '4px solid transparent',
                  backgroundImage: 'linear-gradient(#0E0E0E, #0E0E0E), linear-gradient(135deg, #FF00B8, #FF6B35, #FFB800)',
                  backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box',
                  boxShadow: '0 0 20px rgba(255,0,184,0.15)',
                }}>
                  <span className="text-4xl font-black" style={{
                    fontFamily: "'Bebas Neue', sans-serif",
                    background: 'linear-gradient(135deg, #FF00B8, #FFB800)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  }}>{name.charAt(0)}</span>
                </div>
              )}
              {isChallenger && (
                <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center border-[3px] border-[#080808]" data-testid="card-challenger-badge" style={{
                  background: 'linear-gradient(135deg, #FFB800, #FF6B35)',
                  boxShadow: '0 0 10px rgba(255,184,0,0.5)',
                }}>
                  <Trophy className="w-3.5 h-3.5 text-black" />
                </span>
              )}
            </div>

            {/* Name */}
            <p className="text-[30px] font-black text-white leading-none tracking-[2px]" style={{
              fontFamily: "'Bebas Neue', sans-serif",
              textShadow: '0 2px 8px rgba(0,0,0,0.5)',
            }} data-testid="card-name">{name.toUpperCase()}</p>

            {/* Category */}
            <p className="text-[12px] font-bold tracking-[2px] mt-1" style={{
              fontFamily: "'Bebas Neue', sans-serif",
              background: isChallenger ? 'linear-gradient(90deg, #FFB800, #FF6B35)' : 'linear-gradient(90deg, #FF00B8, #FF6B35)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>{isChallenger ? 'CHALLENGER ACTIF' : role.toUpperCase()}</p>

            {/* Gradient line */}
            <div className="h-[1px] w-3/5 mt-3.5 mb-3" style={{ background: 'linear-gradient(90deg, transparent, #FF00B8, #FF5533, #FFB800, transparent)' }} />

            {/* Details */}
            <div className="flex justify-center gap-8 w-full">
              <div>
                <p className="text-[7px] font-bold tracking-[2px]" style={{ color: 'rgba(255,0,184,0.6)' }}>ADHESION</p>
                <p className="text-[13px] font-bold text-white tracking-[1px] mt-0.5" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                  {memberSince.replace(/\//g, ' / ')}
                </p>
              </div>
              <div>
                <p className="text-[7px] font-bold tracking-[2px]" style={{ color: 'rgba(255,0,184,0.6)' }}>N° ADHERENT</p>
                <p className="text-[13px] font-bold text-white tracking-[1px] mt-0.5" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{memberId}</p>
              </div>
            </div>

            {/* Slogan */}
            <p className="text-[6px] tracking-[4px] text-zinc-600 font-bold mt-4">ENSEMBLE · PLUS FORT</p>

          </div>
        </div>

        </div>
        </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}
